import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useApp } from './contexts/AppContext';
import { AppProvider } from './contexts/AppContext';
import { FilterProvider } from './contexts/FilterContext';
import { ProtectedTestRoute } from './components/ProtectedTestRoute';
import { Layout } from './components/Layout';
import { HomePage } from './pages/HomePage';
import { DashboardPage } from './pages/DashboardPage';
import { TestInstructionsPage } from './pages/TestInstructionsPage';
import { TestPage } from './pages/TestPage';
import { TestResultsPage } from './pages/TestResultsPage';
import { DetailedResultsPage } from './pages/DetailedResultsPage';
import { PrintableResultsPage } from './pages/PrintableResultsPage';
import { TestHistoryPage } from './pages/TestHistoryPage';
import { CustomTestPage } from './pages/CustomTestPage';
import { KnowledgeSprintPage } from './pages/KnowledgeSprintPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { SignIn } from './components/SignIn';
import { SignUp } from './components/SignUp';
import { ProfilePage } from './components/ProfilePage';
import { ExamView } from './components/ExamView';
import { NDAChapterWiseView } from './components/NDAChapterWiseView';
import { NDAPaperWiseView } from './components/NDAPaperWiseView';
import { NDAMockTestView } from './components/NDAMockTestView';
import { JEEMainChapterWiseView } from './components/JEEMainChapterWiseView';
import { JEEMainMockTestView } from './components/JEEMainMockTestView';
import { JEEMainPaperWiseView } from './components/JEEMainPaperWiseView';
import { NEETChapterWiseView } from './components/NEETChapterWiseView';
import { NEETPaperWiseView } from './components/NEETPaperWiseView';
import { NEETMockTestView } from './components/NEETMockTestView';
import { MockTestList } from './components/MockTestList';
import { Robots } from './components/Robots';
import { AuthHandler } from './components/AuthHandler';
import { Sitemap } from './components/Sitemap';
import { CookieConsent } from './components/CookieConsent';
import { PrivacyConsentModal } from './components/PrivacyConsentModal';
import { NetworkStatus } from './components/NetworkStatus';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AdminPage } from './admin/pages/AdminPage';
import { UserDetailPage } from './admin/pages/UserDetailPage';


const App = () => {
  const [showPrivacyModal, setShowPrivacyModal] = React.useState(false);
  const [appReady, setAppReady] = React.useState(false);

  React.useEffect(() => {
    const loader = document.getElementById('critical-loader');
    if (loader) {
      loader.style.display = 'none';
    }
    
    const privacyAccepted = localStorage.getItem('privacyPolicyAccepted');
    if (!privacyAccepted) {
      setShowPrivacyModal(true);
    } else {
      setAppReady(true);
    }
  }, []);

  const handlePrivacyAccept = () => {
    localStorage.setItem('privacyPolicyAccepted', 'true');
    setShowPrivacyModal(false);
    setAppReady(true);
  };

  const handlePrivacyDecline = () => {
    window.location.href = 'https://www.google.com';
  };

  return (
    <ErrorBoundary>
      <HelmetProvider>

    <AppProvider>
      <FilterProvider>
        <Router>
        <RouteLoadingIndicator />
        <AuthHandler />
        <div className="min-h-screen bg-brand-bg text-brand-text-primary transition-all duration-300">
          <Routes>
            {/* Auth Routes */}
            <Route path="/signin" element={<SignIn onNavigateToSignUp={() => window.location.href = '/signup'} />} />
            <Route path="/signup" element={<SignUp onNavigateToSignIn={() => window.location.href = '/signin'} />} />
            
            {/* Special Routes */}
            <Route path="/robots.txt" element={<Robots />} />
            <Route path="/sitemap.xml" element={<Sitemap />} />


            {/* Admin Route */}
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/user/:userId" element={<UserDetailPage />} />
            
            {/* Test Flow Routes */}
            <Route path="/test/:testId/instructions" element={<ProtectedTestRoute><TestInstructionsPage /></ProtectedTestRoute>} />
            <Route path="/test/:testId/start" element={<ProtectedTestRoute><TestPage /></ProtectedTestRoute>} />
            <Route path="/test/:testId/results" element={<ProtectedTestRoute><TestResultsPage /></ProtectedTestRoute>} />
            <Route path="/test/:testId/detailed-results" element={<ProtectedTestRoute><DetailedResultsPage /></ProtectedTestRoute>} />
            <Route path="/test/:testId/print-results" element={<PrintableResultsPage />} />

            <Route path="/custom-test/:testId" element={<ProtectedTestRoute><CustomTestPage /></ProtectedTestRoute>} />
            <Route path="/test/:testId/instructions" element={<TestInstructionsPage />} />
            <Route path="/test/:testId/start" element={<TestPage />} />
            <Route path="/test/:testId/results" element={<TestResultsPage />} />
            <Route path="/test/:testId/detailed-results" element={<DetailedResultsPage />} />
            <Route path="/custom-test/:testId" element={<CustomTestPage />} />
            
            {/* Main App Routes with Layout */}
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            <Route path="/dashboard" element={<Layout><DashboardPage /></Layout>} />
            <Route path="/knowledge-sprint" element={<KnowledgeSprintPage />} />
            <Route path="/test-history" element={<Layout><ProtectedRoute><TestHistoryPage /></ProtectedRoute></Layout>} />
            <Route path="/profile" element={<Layout><ProtectedRoute><ProfilePageWrapper /></ProtectedRoute></Layout>} />
            <Route path="/exam/:examId" element={<Layout><ExamViewWrapper /></Layout>} />
            <Route path="/exam/:examId/mock-tests" element={<Layout><MockTestListWrapper /></Layout>} />
            <Route path="/nda/chapter-wise" element={<Layout><NDAChapterWiseWrapper /></Layout>} />
            <Route path="/nda/paper-wise" element={<Layout><NDAPaperWiseWrapper /></Layout>} />
            <Route path="/nda/mock-tests" element={<Layout><NDAMockTestWrapper /></Layout>} />
            <Route path="/jee-main/chapter-wise" element={<Layout><JEEMainChapterWiseWrapper /></Layout>} />
            <Route path="/jee-main/mock-tests" element={<Layout><JEEMainMockTestWrapper /></Layout>} />
            <Route path="/jee-main/paper-wise" element={<Layout><JEEMainPaperWiseWrapper /></Layout>} />
            <Route path="/neet/chapter-wise" element={<Layout><NEETChapterWiseWrapper /></Layout>} />
            <Route path="/neet/paper-wise" element={<Layout><NEETPaperWiseWrapper /></Layout>} />
            <Route path="/neet/mock-tests" element={<Layout><NEETMockTestWrapper /></Layout>} />
            
            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
            <Route path="/terms-of-service" element={<Layout><TermsOfServicePage /></Layout>} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <CookieConsent />
            <NetworkStatus />
          </div>
          </Router>
        </FilterProvider>
      </AppProvider>
      </HelmetProvider>
    </ErrorBoundary>)


};

// Wrapper components for routes that need additional logic
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useApp();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/signin" replace />;
  }
  
  return children;
};

const ProfilePageWrapper = () => {
  const { user } = useApp();
  return <ProfilePage user={user} />;
};

const ExamViewWrapper = () => {
  const { examId } = useParams();
  const { user } = useApp();
  const navigate = useNavigate();
  
  const handleToggleBookmark = async (questionId) => {
    // Bookmark logic here
  };
  
  return (
    <ExamView 
      examId={examId} 
      user={user} 
      onToggleBookmark={handleToggleBookmark} 
      onNavigateToSignIn={() => navigate('/signin')}
    />
  );
};

const MockTestListWrapper = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  const examMId = examId.replace(/-/g, '')

  const exam = { id: examMId, name: examId?.toUpperCase() || 'EXAM' };
  
  const handleStartTest = (testId) => {
    if (!user) {
      sessionStorage.setItem('redirectPath', `/test/${testId}/instructions`);
      navigate('/signin');
    } else {
      console.log('Starting test:', testId);
      navigate(`/test/${testId}/instructions`);
    }
  };
  
  return (
    <MockTestList 
      exam={exam} 
      onBack={() => navigate('/dashboard')} 
      user={user} 
      onStartTest={handleStartTest}
    />
  );
};

const NDAChapterWiseWrapper = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  
  return (
    <NDAChapterWiseView 
      user={user} 
      onToggleBookmark={() => {}} 
      onNavigateToSignIn={() => navigate('/signin')}
    />
  );
};

const NDAPaperWiseWrapper = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  
  return (
    <NDAPaperWiseView 
      user={user} 
      onToggleBookmark={() => {}} 
      onNavigateToSignIn={() => navigate('/signin')}
    />
  );
};

const NDAMockTestWrapper = () => {
  const navigate = useNavigate();
  const { user } = useApp();
  
  const exam = { id: 'nda', name: 'NDA' };
  
  const handleStartTest = (testId) => {
    if (!user) {
      sessionStorage.setItem('redirectPath', `/test/${testId}/instructions`);
      navigate('/signin');
    } else {
      console.log('Starting NDA test:', testId);
      navigate(`/test/${testId}/instructions`);
    }
  };
  
  return (
    <NDAMockTestView 
      exam={exam} 
      onBack={() => navigate('/dashboard')} 
      onStartTest={handleStartTest}
    />
  );
};

const JEEMainChapterWiseWrapper = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  
  return (
    <JEEMainChapterWiseView 
      user={user} 
      onToggleBookmark={() => {}} 
      onNavigateToSignIn={() => navigate('/signin')}
    />
  );
};

const JEEMainMockTestWrapper = () => {
  const navigate = useNavigate();
  const { user } = useApp();
  
  const exam = { id: 'jee-main', name: 'JEE Main' };
  
  const handleStartTest = (testId) => {
    if (!user) {
      sessionStorage.setItem('redirectPath', `/test/${testId}/instructions`);
      navigate('/signin');
    } else {
      console.log('Starting JEE Main test:', testId);
      navigate(`/test/${testId}/instructions`);
    }
  };
  
  return (
    <JEEMainMockTestView 
      exam={exam} 
      onBack={() => navigate('/dashboard')} 
      onStartTest={handleStartTest}
    />
  );
};

const JEEMainPaperWiseWrapper = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  
  return (
    <JEEMainPaperWiseView 
      user={user} 
      onToggleBookmark={() => {}} 
      onNavigateToSignIn={() => navigate('/signin')}
    />
  );
};

const NEETChapterWiseWrapper = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  
  return (
    <NEETChapterWiseView 
      user={user} 
      onToggleBookmark={() => {}} 
      onNavigateToSignIn={() => navigate('/signin')}
    />
  );
};

const NEETMockTestWrapper = () => {
  const navigate = useNavigate();
  const { user } = useApp();
  
  const exam = { id: 'neet', name: 'NEET' };
  
  const handleStartTest = (testId) => {
    if (!user) {
      sessionStorage.setItem('redirectPath', `/test/${testId}/instructions`);
      navigate('/signin');
    } else {
      console.log('Starting NEET test:', testId);
      navigate(`/test/${testId}/instructions`);
    }
  };
  
  return (
    <NEETMockTestView 
      exam={exam} 
      onBack={() => navigate('/dashboard')} 
      onStartTest={handleStartTest}
    />
  );
};

const NEETPaperWiseWrapper = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  
  return (
    <NEETPaperWiseView 
      user={user} 
      onToggleBookmark={() => {}} 
      onNavigateToSignIn={() => navigate('/signin')}
    />
  );
};


const RouteLoadingIndicator = () => {
  const { routeLoading } = useApp();
  
  if (!routeLoading) return null;
  
  return <div className="route-loading" />;
};

export default App;